import React, { useState, useRef, useContext, useEffect } from 'react'
import { userContext } from 'global/context/sub-contexts/userContext'
import IntersectionVisible from 'components/IntersectionVisible'
import PinterestPins from './PinterestPins'
import './PinterestStep.scss'

//Step slide-specific imports
import Slider from 'react-slick'
import StartSlide from 'steps/StartSlide'
import ArrowButton from 'steps/ArrowButton3'
import SkipButton from 'steps/SkipButton'
import analytics from 'global/AnalyticsData'
import {
  start as serviceStart,
  complete as serviceComplete,
} from 'services/pinterest'
import { stepStyle } from 'util/misc'

const COMPLETE_BY_SKIP = true
const COMPLETE_NO_SKIP = false

const PinterestStep = ({
  step,
  breakId,
  onStepStart,
  onStepComplete,
  onGoToNext,
  onStepSkip,
  scrollComplete,
  unlockNextStep,
  isComplete,
  onAuthRequired,
  onUnhandledError,
  HtmlFooter,
}) => {
  const user_context = useContext(userContext)
  const [stepCompleted, setStepCompleted] = useState(isComplete) //Step progress state
  const [interacted, setInteracted] = useState(isComplete)
  const sliderRef = useRef(null)
  const [stepState, setStepState] = useState('NOT_STARTED')
  const nextArrowEl = useRef(null)

  const sliderSettings = {
    dots: false,
    arrows: false,
    swipe: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    adaptiveHeight: true,
    slidesToScroll: 1,
  }

  const handleNextClick = async stepSkipped => {
    if (!stepCompleted) {
      try {
        const response = await serviceComplete(breakId, step.id, stepSkipped)
        const { totalPoints } = response.detail
        user_context.setPoints(totalPoints)
        if (!stepSkipped) {
          onStepComplete && onStepComplete(step.id)
        }
      } catch (e) {
        console.log(`Error going to next step: ${e}`)
      }
      setStepCompleted(true)
    }
    onGoToNext && onGoToNext(step.id)
  }
  const handleStartService = async function () {
    let response = await serviceStart(breakId, step.id)
    if (onStepStart) {
      const { attempt, firstBreakEngagement } = response
      onStepStart(step, attempt, firstBreakEngagement)
    }
  }
  // called by start slide OR when visible
  const handleStartStep = () => {
    sliderRef.current.slickNext()

    handleStartService()

    setStepState('STARTED')
  }

  // our intersection observer told us we are on screen.
  const handleIsOnScreen = isIntersecting => {
    if (!step.hasStartScreen && isIntersecting && stepState === 'NOT_STARTED') {
      handleStartStep()
    }
  }

  //functionality for start slide.
  const handleStartClicked = () => {
    handleStartStep()
  }

  // an onload if we need it
  const pinterestLoaded = () => {
    // console.log("[Pinterest Step] Pinterest step start")
  }

  //listen for scrollComplete and fire off handleCompleteClick when toggled
  useEffect(() => {
    if (scrollComplete && !stepCompleted) {
      handleNextClick(COMPLETE_BY_SKIP)
      setArrowLocked()
    }
  }, [scrollComplete, stepCompleted])

  const setArrowLocked = () => {
    nextArrowEl.current.disabled = true
  }

  const handleStepSkip = () => {
    console.log('[Pinterest Step] Step Skipped')
    onStepSkip(step.id)
    handleNextClick(COMPLETE_BY_SKIP)
  }

  //This step is triggered complete when pins are intereacted with.
  const onPinInteract = pinID => {
    analytics.stepEvent('step_interaction_pinterest_pin_click', step, {
      result: `https://www.pinterest.com/pin/${pinID}/`,
      result_id: pinID,
    })
    setInteracted(true)
    if (stepState !== 'COMPLETE') {
      setStepState('COMPLETE')
    }
    unlockNextStep && unlockNextStep(step.id)
  }

  //Fixes errors on build. Also need to make sure these pinids are formatted correctly before we pass them to component.
  const getPinterestData = () => {
    if (step.pinterest) {
      let pinterestData = step.pinterest
      pinterestData.pinIDs = pinterestData.pinIds.split(' ').join('').split(',')
      return pinterestData
    } else {
      return false
    }
  }
  const pinterestData = getPinterestData()

  return (
    <>
      <div className="sponsored-flow-panel" style={stepStyle(step)}>
        <IntersectionVisible
          active={true}
          options={{
            threshold: 0.4,
          }}
          onIntersect={handleIsOnScreen}
        >
          <Slider
            ref={slider => (sliderRef.current = slider)}
            {...sliderSettings}
            className="pins__slider"
          >
            {step.hasStartScreen && (
              <StartSlide
                stepType={step.type}
                headline={step.startHeader || step.headline}
                para={step.startBody}
                footer={step.startFooter}
                backgroundImageId={step.startBackgroundImageId}
                backgroundColor={step.startBackgroundColor}
                onStartClicked={handleStartClicked}
              />
            )}
            {pinterestData && (
              <div className="pinterest-step--slide">
                <section className="pinterest-step-container">
                  <div className={`pinterest-step--copy`}>
                    {pinterestData.headline && (
                      <h2 className="pinterest-step--headline">
                        {pinterestData.headline}
                      </h2>
                    )}
                    {pinterestData.introCopy && (
                      <h3 className="pinterest-step--subhead">
                        {pinterestData.introCopy}
                      </h3>
                    )}
                    {pinterestData.bodyCopy && (
                      <p className="pinterest-step--body">
                        {pinterestData.bodyCopy}
                      </p>
                    )}
                  </div>
                  <PinterestPins
                    onLoad={pinterestLoaded}
                    onPinInteract={onPinInteract}
                    pinIDs={pinterestData.pinIDs}
                  />
                  {stepState !== 'NOT_STARTED' &&
                    !interacted &&
                    step.showSkipButton && (
                      <SkipButton onClick={handleStepSkip} step_id={step.id} />
                    )}
                </section>
              </div>
            )}
          </Slider>

          <ArrowButton
            visible={stepState !== 'NOT_STARTED'}
            clickable={interacted}
            onClick={e => {
              handleNextClick(COMPLETE_NO_SKIP)
            }}
          />
          {HtmlFooter && <HtmlFooter />}
        </IntersectionVisible>
      </div>
    </>
  )
}
export default PinterestStep
